import { useCallback, useContext, useState } from "react";
import { LoginContext } from "../../src/contexts/login_context";
import { useSearchPagination } from "../../src/contexts/Ncm&Gpc_context";
import { consultarGpcNcm } from "../../src/services/consultaGPCNCM";
import { dismissToast, showError, showWait } from "../../src/services/toast";
import { stripCnpj } from "../../src/helpers/maskcpfcnpj";

export type SearchParams = {
  country?: string;
  code?: string | number | null | undefined;
  valueCNPJ?: string;
  onAfterSearch?: (result: {
    raw: any;
    produtos: any[];
    maxPages: number;
    info: any;
  }) => void;
  onRequireConfirmation?: (data: { filtros: any; raw: any }) => void;
};

export function useGpcNcmSearch() {
  const { jwt } = useContext(LoginContext);
  const {
    selectedDatasetPage,
    setDatapagina,
    setStepMaganer,
    setSelectedDatasetPage,
  } = useSearchPagination();
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async ({
      country,
      code,
      valueCNPJ,
      onAfterSearch,
      onRequireConfirmation,
    }: SearchParams) => {
      if (!country || code === undefined || code === null) return;

      const isCnpjMode = country === "CNPJ";
      const hasCnpjFilter = !isCnpjMode && Boolean(valueCNPJ && valueCNPJ.trim());

      const filtros: any = {
        ...(isCnpjMode
          ? { cnpj: stripCnpj(String(code ?? "")) }
          : {
              [country.toLowerCase()]: code,
              ...(hasCnpjFilter ? { cnpj: stripCnpj(valueCNPJ ?? "") } : {}),
            }),
        ...(selectedDatasetPage != null ? { pagina: selectedDatasetPage } : {}),
      };

      const toastId = showWait("Aguarde, enquanto realizamos sua pesquisa");
      setLoading(true);

      try {
        const r = await consultarGpcNcm(jwt, filtros);
        console.log("Resposta da consulta GPC/NCM:", r);

        if (r?.result?.requerConfirmacao) {
          onRequireConfirmation?.({ filtros, raw: r.result });

          setDatapagina([]);
          setStepMaganer("list");
          setSelectedDatasetPage(0);
          return;
        }

        if (Array.isArray(r?.result)) {
          const limitObj = r.result.find(
            (x: any) =>
              x && typeof x === "object" && "mensagemLimite" in x
          ) as { mensagemLimite?: string } | undefined;

          if (limitObj?.mensagemLimite) {
            showError(limitObj.mensagemLimite);
            setDatapagina([]);
            setStepMaganer("list");
            setSelectedDatasetPage(0);
            return;
          }
        }

        if (r?.success && r?.result) {
          const { produtos, maxPages, info } = normalizeGpcNcmResponse(
            r.result
          );

          if (!produtos || produtos.length === 0) {
            setDatapagina([]);
            setStepMaganer("list");

            onAfterSearch?.({
              raw: r.result,
              produtos: [],
              maxPages,
              info,
            });

            showError("Nenhum resultado encontrado nessa lista");
            setSelectedDatasetPage(0);
            return;
          }

          setDatapagina(produtos);
          setStepMaganer("table");

          onAfterSearch?.({ raw: r.result, produtos, maxPages, info });
          setSelectedDatasetPage(0);
        } else {
          showError("Nada encontrado nessa lista");
        }
      } catch (error: unknown) {
        showError("Nada encontrado");
      } finally {
        dismissToast(toastId);
        setLoading(false);
      }
    },
    [jwt, selectedDatasetPage, setDatapagina, setStepMaganer, setSelectedDatasetPage]
  );

  return { loading, search };
}

function normalizeGpcNcmResponse(
  raw: any
): { produtos: any[]; maxPages: number; info: any } {
  let produtos: any[] = [];
  let maxPages = 0;
  let info: any = null;

  if (Array.isArray(raw)) {
    const arrProdutos = raw.find((x) => Array.isArray(x)) || [];
    info = raw.find((x) => !Array.isArray(x)) || null;
    produtos = Array.isArray(arrProdutos) ? arrProdutos : [];
    maxPages =
      Number(
        info?.MAX_PAGINA_CNPJ ??
          info?.MAX_PAGINA_GPC ??
          info?.MAX_PAGINA_NCM ??
          0
      ) || 0;
  } else if (raw && Array.isArray(raw.data)) {
    produtos = raw.data;
    maxPages = 0;
  }

  return { produtos, maxPages, info };
}
